import React, { useCallback, useMemo, useState } from "react";
import { useFragment } from "react-relay";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faLanguage } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { graphql, type PayloadError } from "relay-runtime";

import { commitMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Button, Tooltip } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import Selector, { type SingleValue } from "~/ui/selectors/Selector";

import {
	getAllLanguageOptionsAvailableForTranscription,
	getLanguageInformationForCode,
	languageCodeIsSupported,
	languageIsEnglish,
	type TranscriptLanguageCodeType,
	type TranscriptLanguageOption,
} from "./languageUtils";

import type { RegenerateForLanguageModalFragment_meetingRecording$key } from "~/__generated__/RegenerateForLanguageModalFragment_meetingRecording.graphql";
import type {
	RegenerateForLanguageModalMutation,
	RegenerateForLanguageModalMutation$data,
} from "~/__generated__/RegenerateForLanguageModalMutation.graphql";

import styles from "./css/RegenerateForLanguageModal.module.css";

export const regenerateForLanguageModalFragment = graphql`
	fragment RegenerateForLanguageModalFragment_meetingRecording on MeetingRecording {
		id
		detectedLanguage
		languageConfidenceScore
		canRegenerateSummary
		regenerationForLanguageIsInProgress
	}
`;

const regenerateForLanguageMutation = graphql`
	mutation RegenerateForLanguageModalMutation($meetingRecordingId: ID!, $languageCode: String!) {
		regenerateMeetingTranscriptAndSummaryForLanguage(
			meetingRecordingId: $meetingRecordingId
			languageCode: $languageCode
		) {
			meetingRecording {
				...RegenerateForLanguageModalFragment_meetingRecording
			}
		}
	}
`;

interface RegenerateForLanguageModalProps {
	meetingRecording: RegenerateForLanguageModalFragment_meetingRecording$key;
	close(regenerating: boolean): void;
	noteId: string;
}

function removeInitialLanguageFromOptions(
	options: TranscriptLanguageOption[],
	code: TranscriptLanguageCodeType,
): TranscriptLanguageOption[] {
	if (!languageIsEnglish(code)) {
		return options.filter(option => option.value !== code);
	}
	return options.filter(option => !languageIsEnglish(option.value));
}

export default function RegenerateForLanguageModal({
	meetingRecording: meetingRecordingKey,
	close,
	noteId,
}: RegenerateForLanguageModalProps) {
	const meetingRecording = useFragment(regenerateForLanguageModalFragment, meetingRecordingKey);
	const onClose = useCallback(
		(regenerating: boolean) => {
			close(regenerating);
		},
		[close],
	);

	const cancel = useCallback(() => {
		onClose(false);
	}, [onClose]);

	const initialLanguage = meetingRecording.detectedLanguage;
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const canRegenerateSummary = useMemo(() => {
		if (meetingRecording.canRegenerateSummary === false) {
			setErrorMessage("You've reached the maximum number of regenerations for this meeting.");
		} else if (meetingRecording.regenerationForLanguageIsInProgress) {
			setErrorMessage("Another regeneration is currently in progress for this meeting.");
		} else {
			setErrorMessage(null);
		}
		return meetingRecording.canRegenerateSummary && !meetingRecording.regenerationForLanguageIsInProgress;
	}, [meetingRecording.canRegenerateSummary, meetingRecording.regenerationForLanguageIsInProgress]);

	const initialLanguageInformation = useMemo(() => {
		if (initialLanguage !== null && languageCodeIsSupported(initialLanguage)) {
			return getLanguageInformationForCode(initialLanguage as TranscriptLanguageCodeType);
		}
		return null;
	}, [initialLanguage]);

	const languageOptions = useMemo(() => {
		let allOptions = getAllLanguageOptionsAvailableForTranscription();
		if (initialLanguage === null) {
			return allOptions;
		}

		// remove the initial language from the list of options
		allOptions = allOptions.filter(option => option.value !== initialLanguage);

		if (languageCodeIsSupported(initialLanguage)) {
			allOptions = removeInitialLanguageFromOptions(allOptions, initialLanguage);
		}
		return allOptions;
	}, [initialLanguage]);

	const [selectedLanguage, setSelectedLanguage] = useState<TranscriptLanguageOption | null>();

	const onSelectedLanguageChange = useCallback((lang: SingleValue<TranscriptLanguageOption>) => {
		setSelectedLanguage(lang);
	}, []);

	const onRegenerateForLanguage = useCallback(() => {
		if (!selectedLanguage) return;

		commitMutationPromise<RegenerateForLanguageModalMutation>(environment, {
			mutation: regenerateForLanguageMutation,
			variables: {
				meetingRecordingId: meetingRecording.id,
				languageCode: selectedLanguage.value,
			},
			onCompleted: (
				_resp: RegenerateForLanguageModalMutation$data,
				errors: readonly PayloadError[] | null | undefined,
			) => {
				if (errors) {
					setErrorMessage(`An error occurred while regenerating: ${errors[0].message}`);
					return;
				} else {
					dispatchAnalyticsTrack("[AI Copilot] Transcript and summary regenerating for language", {
						meetingRecordingId: meetingRecording.id,
						recordingId: meetingRecording.id,
						noteId: noteId,
						detectedLanguage: meetingRecording.detectedLanguage,
						languageConfidenceScore: meetingRecording.languageConfidenceScore,
						selectedLanguage: selectedLanguage.value,
					});
				}
				onClose(true);
			},
			onError: (error: Error) => {
				setErrorMessage(error.message);
			},
		});
	}, [
		selectedLanguage,
		meetingRecording.id,
		meetingRecording.detectedLanguage,
		meetingRecording.languageConfidenceScore,
		onClose,
		noteId,
	]);

	return (
		<Modal onClose={cancel} className={styles.modal}>
			<Modal.Header className={styles.header} noBottomBorder onClose={cancel}>
				<Modal.Title>Transcript Language</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.content}>
				{initialLanguageInformation?.nativeName && (
					<div className={styles.currentLanguageDiv}>
						<FontAwesomeIcon icon={faLanguage} /> Currently transcribed in {initialLanguageInformation.nativeName}
						{!languageIsEnglish(initialLanguageInformation.code) && ` (${initialLanguageInformation.englishName})`}
					</div>
				)}
				<div className={styles.selectLanguageDiv}>
					<span className={styles.selectorLabel}>
						Re-transcribe meeting in{" "}
						<Tooltip
							placement="auto"
							content={
								<>
									Changing the language will regenerate the transcript and summary for the given language. It is{" "}
									<strong>not</strong> intended to translate the transcript into a language that was not spoken.
								</>
							}
						>
							<FontAwesomeIcon icon={faInfoCircle} fixedWidth />
						</Tooltip>
					</span>
					<Selector
						autoFocus
						isSearchable
						placeholder="Select a language…"
						options={languageOptions}
						value={selectedLanguage}
						onChange={onSelectedLanguageChange}
					/>
					<span className={styles.selectorDescription}>
						Note: the Recap and Transcript willl take a couple of minutes to regenerate.
					</span>
				</div>
				{errorMessage && <div className={styles.errorMessageContainer}>{errorMessage}</div>}
			</Modal.Body>
			<Modal.Footer noTopBorder>
				<Button size="normal" variant="secondary" onClick={cancel}>
					Cancel
				</Button>
				<Button
					size="normal"
					variant="primary"
					disabled={!selectedLanguage || !canRegenerateSummary}
					onClick={onRegenerateForLanguage}
				>
					Confirm
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
